import React, { useState, useCallback, useEffect } from 'react';
import { kpiApi } from '../../services/api';
import { useDbNavigate } from '../../context/DatabaseContext';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import {
  FileText,
  Building2,
  Network,
  Zap,
  ClipboardList,
  BarChart3,
  History,
  Cog
} from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import DashboardTile from '../../components/common/DashboardTile';
import { PieTooltip, BarTooltip } from '../../components/common/ChartTooltips';

/**
 * DocumentationDashboardPage - KPI overview for documentation metrics
 */
const DocumentationDashboardPage = () => {
  const navigate = useDbNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState(null);

  const loadKpis = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await kpiApi.getDocumentation();
      setKpis(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch when database selection changes
  useEffect(() => {
    const handler = () => loadKpis();
    window.addEventListener('pmd-database-changed', handler);
    return () => window.removeEventListener('pmd-database-changed', handler);
  }, [loadKpis]);

  // Composite icon: main icon with small lightning bolt overlay
  const PowerIcon = ({ icon: MainIcon, size = 24 }) => (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <MainIcon size={size} />
      <Zap size={size * 0.45} style={{ position: 'absolute', bottom: -2, right: -4, fill: 'currentColor' }} />
    </span>
  );

  // Simple stat card (no chart, just a big number)
  const StatCard = ({ value, label, icon: Icon, color = 'var(--color-primary)', compositeIcon }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ color }}>
        {compositeIcon || <Icon size={24} />}
      </div>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );

  // Status colors for IP address donuts
  const statusColors = {
    'Active': '#22c55e',
    'Pending': '#f59e0b',
    'Inactive': '#94a3b8',
    'Spare': '#94a3b8',
    'Pending Connection': '#f59e0b',
    'Reserved': '#3b82f6',
    'Future': '#8b5cf6',
    'De-Activated': '#ef4444',
    'Unknown': '#e2e8f0'
  };

  const getStatusColor = (status) => statusColors[status] || statusColors['Unknown'];

  // Convert grouped counts to recharts format
  const toChartData = (grouped) => {
    if (!grouped || grouped.length === 0) return [];
    return grouped.filter(g => g.count > 0).map(g => ({
      name: g.key,
      value: g.count,
      fill: getStatusColor(g.key)
    }));
  };

  // Mini donut for IP sections
  const MiniDonut = ({ data, label }) => {
    const chartData = toChartData(data);
    const total = chartData.reduce((sum, d) => sum + d.value, 0);

    return (
      <div className="ip-section">
        <h4>{label}</h4>
        {chartData.length > 0 ? (
          <ResponsiveContainer width={90} height={90}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={24}
                outerRadius={40}
                paddingAngle={1}
                animationDuration={600}
              >
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} stroke="none" />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className="recharts-donut-label-sm">
                {total.toLocaleString()}
              </text>
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="donut-chart empty" style={{ width: 80, height: 80 }}>
            <span className="donut-center" style={{ width: 56, height: 56 }}>0</span>
          </div>
        )}
        <div className="ip-legend">
          {data?.map((g, i) => (
            <span key={i} className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: getStatusColor(g.key) }} />
              {g.key}: {g.count.toLocaleString()}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Calculate combined IP address totals
  const getIpAddressTotal = () => {
    if (!kpis) return 0;
    const slnTotal = kpis.ipAddressSln?.reduce((sum, g) => sum + g.count, 0) || 0;
    const dlnTotal = kpis.ipAddressDln?.reduce((sum, g) => sum + g.count, 0) || 0;
    const serialTotal = kpis.networksSerial?.reduce((sum, g) => sum + g.count, 0) || 0;
    return slnTotal + dlnTotal + serialTotal;
  };

  // Prepare bar chart data for drawings by building
  const buildingBarData = (kpis?.drawingsByBuilding || []).slice(0, 8).map(d => ({
    name: d.key,
    count: d.count
  }));

  return (
    <DashboardLayout
      id="documentation"
      title="Documentation Dashboard"
      icon={BarChart3}
      onRefresh={loadKpis}
      loading={loading}
      error={error}
    >
      {/* Row 1: Three tiles across */}
      <DashboardTile
        title="Drawings"
        icon={FileText}
        onClick={() => navigate('/drawings')}
      >
        <div className="tile-stat-row">
          <StatCard value={kpis?.drawings || 0} label="Total Drawings" icon={FileText} color="#3b82f6" />
          <StatCard value={kpis?.drawingLogs || 0} label="Log Entries" icon={History} color="#6366f1" />
          <StatCard value={kpis?.drawingRevisions || 0} label="Revisions" icon={History} color="#8b5cf6" />
        </div>
      </DashboardTile>

      <DashboardTile
        title="Drawings by Building"
        icon={Building2}
      >
        {buildingBarData.length > 0 ? (
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={buildingBarData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip content={<BarTooltip />} />
              <Bar dataKey="count" fill="#3b82f6" radius={[3, 3, 0, 0]} animationDuration={600} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="bar-chart-empty">No data</div>
        )}
      </DashboardTile>

      <DashboardTile
        title="Power Distribution"
        icon={Zap}
      >
        <div className="tile-stat-row">
          <StatCard value={kpis?.pwrDist || 0} label="Power Feeds" icon={Zap} color="#f59e0b" compositeIcon={<PowerIcon icon={Zap} />} />
        </div>
      </DashboardTile>

      {/* Row 2 */}
      <DashboardTile
        title="Sequences of Operation"
        icon={ClipboardList}
      >
        <div className="tile-stat-row">
          <StatCard value={kpis?.sooIndex || 0} label="Total SOOs" icon={ClipboardList} color="#10b981" />
          <StatCard value={kpis?.sooRevisions || 0} label="Revisions" icon={History} color="#14b8a6" />
          <StatCard value={kpis?.sooEquipment || 0} label="Equipment" icon={Cog} color="#06b6d4" compositeIcon={<PowerIcon icon={Cog} />} />
        </div>
      </DashboardTile>

      <DashboardTile
        title="Network Addresses"
        icon={Network}
        onClick={() => navigate('/networking-dashboard')}
        colSpan={4}
      >
        <div className="ip-address-grid">
          <MiniDonut data={kpis?.ipAddressSln} label="Site Level (SLN)" />
          <MiniDonut data={kpis?.ipAddressDln} label="Device Level (DLN)" />
          <MiniDonut data={kpis?.networksSerial} label="Serial Networks" />
          <div className="ip-total">
            <span className="ip-total-value">{getIpAddressTotal().toLocaleString()}</span>
            <span className="ip-total-label">Total Addresses</span>
          </div>
        </div>
      </DashboardTile>
    </DashboardLayout>
  );
};

export default DocumentationDashboardPage;
